import {useContext, useState} from 'react';
import {
    ProFormDigit,
    ProFormGroup,
    ProFormItem,
    ProFormList,
    ProFormRadio,
    ProFormSelect,
    ProFormSwitch,
    ProFormText,
    ProFormTimePicker,
    ProFormUploadButton,
} from '@ant-design/pro-form';
import {Badge, Button, message, Tooltip} from 'antd';
import {DeleteOutlined, DownCircleOutlined, UpCircleOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import axiosApiInstance from '../api/axiosClient';
import axios from 'axios';
import {useAtom} from 'jotai';
import {checksEnabled, fetchArtistOptions} from './AddEntryForm';
import {DigitalAssetPageContext} from './DigitalAssetsPage';
import {defaultSelectionsAtom,} from '../CRM/pages/DDEXPage/ddex_atoms';
import {countryOptions, languageOptions} from '../common/ddex_options'

dayjs.extend(duration);

const formatDuration = (duration: number) => {
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    const milliseconds = Math.round((duration % 1) * 1000);
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');
    const formattedMilliseconds = String(milliseconds).padStart(3, '0');
    return `${formattedMinutes}:${formattedSeconds}.${formattedMilliseconds}`;
};

const parseDuration = (durationString: string) => {
    if (!isNaN(+durationString)) {
        return dayjs.duration(+durationString);
    }
    if (!durationString) {
        return dayjs.duration(0);
    }
    const [minutes, secondsWithMillis] = durationString.split(':');
    const [seconds, milliseconds] = secondsWithMillis.split('.');
    return dayjs.duration({
        minutes: parseInt(minutes, 10),
        seconds: parseInt(seconds, 10),
        milliseconds: parseInt(milliseconds, 10),
    });
};

const AddFormSecondPage = ({edit}: { edit: boolean }) => {
    const {firstFormPageRef, secondFormPageRef, listRef, artists, stepFormRef} = useContext(DigitalAssetPageContext);
    const [removingFiles, setRemovingFiles] = useState<number[]>([]);
    const [defaultSelections] = useAtom(defaultSelectionsAtom);

    return (
        <ProFormList<API.MusicDistribution.ArtTrack>
            name='art_tracks'
            initialValue={[{...defaultSelections}]}
            min={1}
            actionRef={listRef}
            colProps={{span: 24}}
            creatorRecord={() => ({...defaultSelections})}
            creatorButtonProps={{
                position: 'bottom',
                creatorButtonText: 'New Track',
                style: {marginTop: 15},
            }}
            actionRender={() => []}
        >
            {(f, index, action) => (
                <div key='isrc'>
                    <ProFormGroup align='center' direction='horizontal' colProps={{span: 24}}
                                  rowProps={{justify: 'end'}}>
                        <ProFormItem label='#' wrapperCol={{span: 10}}>
                            <Badge color='#002288' size='small' count={index + 1}/>
                        </ProFormItem>
                        <ProFormDigit
                            key='volume_number'
                            name='volume_number'
                            label='Vol. No.'
                            min={1}
                            initialValue={index === 0 ? 1 : listRef?.current?.get(index - 1).volume_number}
                            fieldProps={{
                                onChange(value) {
                                    action?.setCurrentRowData({track_number: 1});
                                },
                            }}
                            width={60}
                        />
                        <ProFormDigit
                            key='track_number'
                            name='track_number'
                            label='Track No.'
                            initialValue={index === 0 ? 1 : listRef?.current?.get(index - 1).track_number + 1}
                            min={1}
                            width={60}
                        />
                        <ProFormText
                            key='isrc'
                            name='isrc'
                            label='ISRC'
                            placeholder={'Leave Blank for New ISRC'}
                            width={140}
                            transform={(value) => value?.replace(/-/g, '')}
                            rules={[
                                {
                                    validateTrigger: 'onFinish',
                                    required: checksEnabled,
                                    validator(rule, value: string, resolve) {
                                        if (value === '') {
                                            return resolve();
                                        }
                                        if (!/^[A-Z]{2}\-?[A-Z0-9]{3}\-?[0-9]{2}\-?[0-9]{5}/.test(value)) {
                                            return resolve('Invalid ISRC (formatted incorrectly)');
                                        }
                                        const sanitized = value.replace(/-/g, '');
                                        if (sanitized.length !== 12) {
                                            return resolve('Invalid ISRC (length must be 12)');
                                        }
                                        resolve();
                                    },
                                },
                            ]}
                        />
                        <ProFormText
                            name='title'
                            label='Track Name'
                            width='md'
                            labelCol={{span: 0}}
                            wrapperCol={{span: 24}}
                            placeholder={'Track Name'}
                            required
                            rules={[{required: checksEnabled}]}
                        />
                        {/* <ProFormSelect
              name= "track_primary_genre"
              label='Track Genre'
              width='sm'
              labelCol={{ span: 0 }}
              wrapperCol={{ span: 24 }}
              placeholder={'Track Genre'}
              mode='multiple'
              options={genreOptions}
                        showSearch
                        fieldProps={{
                          onChange(value, option) {
                            firstFormPageRef?.current?.setFieldValue('sub_genre', undefined);
                          },
                          onClear() {
                            firstFormPageRef?.current?.setFieldValue('sub_genre', undefined);
                          },
                        }}
              required
              rules={[{ required: checksEnabled }]}
            />
            <ProFormSelect
            name="track_label"
            label="Track Label"
            width='sm'
            labelCol={{ span: 0 }}
            wrapperCol={{ span: 24 }}
            placeholder={'Track Label'}
            mode='multiple'
            request={fetchLabelOptions}
            fieldProps={{
              showSearch: true,
              dropdownRender(menu) {
                return (
                  <>
                    {menu}
                    <NewLabelForm stepFormRef={stepFormRef} />
                  </>
                );
              },
            }}
            required
              rules={[{ required: checksEnabled }]}
            /> */}
                        <ProFormText
                            name='subtitle'
                            label='Subtitle'
                            width='md'
                            labelCol={{span: 0}}
                            wrapperCol={{span: 24}}
                            placeholder={'Subtitle'}
                        />
                        <ProFormSelect
                            label='Type'
                            initialValue={'ORIGINAL'}
                            allowClear={false}
                            name='track_type'
                            options={['ORIGINAL', 'KARAOKE', 'MEDLEY', 'COVER', 'COVER BY BAND']}
                        />
                        <ProFormTimePicker
                            label='Duration'
                            fieldProps={{format: 'mm:ss.SSS'}}
                            name='duration'
                            width='sm'
                            convertValue={(value) => {
                                if (dayjs.isDayjs(value)) {
                                    return value.format('mm:ss.SSS');
                                } else if (!isNaN(+value)) {
                                    return formatDuration(value / 1000000000);
                                } else {
                                    return value;
                                }
                            }}
                            transform={(value) => {
                                if (!isNaN(+value)) {
                                    return {duration: value};
                                }
                                const duration = parseDuration(value);
                                return {duration: duration.asMilliseconds() * 1000000};
                            }}
                            // rules={[{ required: checksEnabled }]}
                            hidden
                        />
                        <ProFormText
                            key='filename'
                            name='filename'
                            label='Filename'
                            width='sm'
                            labelCol={{span: 0}}
                            wrapperCol={{span: 24}}
                            placeholder={'Filename'}
                            hidden
                            // rules={[{ required: checksEnabled }]}
                        />
                        <ProFormUploadButton
                            label='Audio File'
                            name='audio_file'
                            title={'Upload Audio File'}
                            max={1}
                            fieldProps={{
                                beforeUpload: (file) => {
                                    const audio = new Audio(URL.createObjectURL(file));
                                    audio.addEventListener('loadedmetadata', () => {
                                        const durationFormatted = formatDuration(audio.duration);
                                        const dateObj = dayjs(durationFormatted, 'mm:ss.SSS');
                                        secondFormPageRef?.current?.setFieldValue(['art_tracks', index, 'duration'], dateObj);
                                    });
                                    secondFormPageRef?.current?.setFieldValue(['art_tracks', index, 'filename'], file.name);
                                    return true;
                                },
                                customRequest: async ({file, onSuccess, onError, onProgress}) => {
                                    if (!edit) {
                                        onSuccess('ok');
                                        return;
                                    }
                                    try {
                                        const response = await axiosApiInstance.post(
                                            '/api/digitalassets/generate-archiving-url-upload',
                                            {
                                                fileName: (file as any).name,
                                                fileType: (file as any).type,
                                                folderName: firstFormPageRef?.current?.getFieldValue('upc_ean'),
                                            }
                                        );
                                        const {presignedUrl} = response.data;
                                        return axios
                                            .put(presignedUrl, file, {
                                                headers: {'Content-Type': (file as any).type},
                                                onUploadProgress: (progressEvent) => {
                                                    const percentCompleted = Math.round(
                                                        (progressEvent.loaded * 100) / progressEvent.total
                                                    );
                                                    onProgress({percent: percentCompleted});
                                                },
                                            })
                                            .then(() => {
                                                secondFormPageRef?.current?.setFieldValue(['art_tracks', index, 'is_uploaded'], true);
                                                message.success('File uploaded successfully');
                                                onSuccess('ok');
                                            })
                                            .catch((err) => {
                                                console.error('Error uploading file:', err);
                                                message.error('Error uploading file');
                                                onError(err);
                                            });
                                    } catch (err) {
                                        console.error('Error uploading file:', err);
                                        message.error('Error uploading file');
                                        onError(err);
                                    }
                                },
                                onRemove: async (file) => {
                                    if (!edit) {
                                        return;
                                    }
                                    try {
                                        const response = await axiosApiInstance.post(
                                            '/api/digitalassets/generate-archiving-delete',
                                            {
                                                fileName: (file as any).name,
                                                folderName: firstFormPageRef?.current?.getFieldValue('upc_ean'),
                                            }
                                        );
                                        const {presignedUrl} = response.data;
                                        return axios.delete(presignedUrl);
                                    } catch (err) {
                                        console.error('Error removing file:', err);
                                        message.error('Error removing file');
                                    }
                                    if (!removingFiles.includes(index)) setRemovingFiles([...removingFiles, index]);
                                    return axiosApiInstance
                                        .post('/api/digitalassets/generate-archiving-delete-object', {
                                            fileName: (file as any).name,
                                            folderName: firstFormPageRef?.current?.getFieldValue('upc_ean'),
                                        })
                                        .then(() => {
                                            setRemovingFiles(removingFiles.filter((i) => i !== index));
                                            secondFormPageRef?.current?.setFieldValue(['art_tracks', index, 'is_uploaded'], false);
                                        });
                                },
                                disabled: removingFiles.includes(index),
                                locale: {
                                    downloadFile: 'Download',
                                    previewFile: 'Preview',
                                    removeFile: 'Remove',
                                    uploadError: 'Upload Error',
                                    uploading: 'Uploading...',
                                },
                                showUploadList: {},
                                onDownload: async (file) => {
                                    try {
                                        const response = await axiosApiInstance.post(
                                            '/api/digitalassets/generate-archiving-get',
                                            {
                                                fileName: (file as any).name,
                                                folderName: firstFormPageRef?.current?.getFieldValue('upc_ean'),
                                            }
                                        );
                                        const {presignedUrl} = response.data;
                                        window.open(presignedUrl, '_blank');
                                    } catch (err) {
                                        console.error('Error downloading file:', err);
                                        message.error('Error downloading file');
                                    }
                                },
                                listType: 'text',
                            }}
                            convertValue={(value) => {
                                if (
                                    secondFormPageRef?.current?.getFieldValue(['art_tracks', index, 'is_uploaded']) &&
                                    !value
                                ) {
                                    return [
                                        {
                                            uid: index,
                                            name: secondFormPageRef?.current?.getFieldValue(['art_tracks', index, 'filename']),
                                            status: 'done',
                                        },
                                    ];
                                }
                                return value;
                            }}
                            required
                            rules={[{required: checksEnabled}]}
                        />
                        <ProFormSwitch hidden name={'is_uploaded'}/>
                        <ProFormGroup>
                            <ProFormItem label=' '>
                                <Button
                                    type='link'
                                    size='small'
                                    style={{marginRight: -4, marginLeft: -10}}
                                    icon={
                                        !!action.getCurrentRowData().artists_group_collapsed ? (
                                            <Tooltip title='Configure Artists'>
                                                <DownCircleOutlined/>
                                            </Tooltip>
                                        ) : (
                                            <UpCircleOutlined/>
                                        )
                                    }
                                    onClick={() => {
                                        const collaped = !!action.getCurrentRowData().artists_group_collapsed;
                                        action.setCurrentRowData({artists_group_collapsed: !collaped});
                                    }}
                                />
                            </ProFormItem>
                            <ProFormItem label=' '>
                                <Tooltip title='Delete Row'>
                                    <Button
                                        type='link'
                                        size='small'
                                        style={{marginRight: -4, marginLeft: -26}}
                                        icon={<DeleteOutlined/>}
                                        disabled={listRef?.current?.getList().length < 2}
                                        danger
                                        onClick={() => {
                                            action.remove(index);
                                        }}
                                    />
                                </Tooltip>
                            </ProFormItem>
                            <ProFormSwitch initialValue={true} name={'artists_group_collapsed'} hidden/>
                        </ProFormGroup>
                    </ProFormGroup>
                    <ProFormGroup
                        colProps={{span: 24}}
                        direction='horizontal'
                        collapsible
                        collapsed={!!action.getCurrentRowData().artists_group_collapsed}
                    >
                        <ProFormGroup colProps={{span: 12}} direction='vertical'>
                            <ProFormSelect
                                label={'Main Artists'}
                                name={'main_artists'}
                                mode='multiple'
                                width={'lg'}
                                request={fetchArtistOptions}
                            />
                            <ProFormSelect
                                label='Featuring Artists'
                                name={'featuring_artists'}
                                mode='multiple'
                                width={'lg'}
                                request={fetchArtistOptions}
                            />
                            <ProFormSelect
                                label='Arrangers'
                                name={'arrangers'}
                                mode='multiple'
                                width={'lg'}
                                request={fetchArtistOptions}
                            />
                            <ProFormSelect
                                label='Authors'
                                name={'authors'}
                                mode='multiple'
                                width={'lg'}
                                request={fetchArtistOptions}
                            />
                        </ProFormGroup>
                        <ProFormGroup colProps={{span: 12}} direction='vertical'>
                            <ProFormSelect
                                label='Producers'
                                name={'producers'}
                                mode='multiple'
                                width={'lg'}
                                request={fetchArtistOptions}
                            />
                            <ProFormSelect
                                label='Publishers'
                                name={'publishers'}
                                mode='multiple'
                                width={'lg'}
                                request={fetchArtistOptions}
                            />
                            <ProFormSelect
                                label='Remixers'
                                name={'remixers'}
                                mode='multiple'
                                width={'lg'}
                                request={fetchArtistOptions}
                            />
                            <ProFormSelect
                                label='Composers'
                                name={'composers'}
                                mode='multiple'
                                width={'lg'}
                                request={fetchArtistOptions}
                            />
                        </ProFormGroup>
                        <ProFormGroup colProps={{span: 24}} direction='horizontal'>
                            <ProFormSelect
                                label='Title Language'
                                name='title_language'
                                allowClear={false}
                                width={'sm'}
                                initialValue={'GR'}
                                options={countryOptions}
                                showSearch
                            />
                            <ProFormSelect
                                label='Lyrics Language'
                                name='lyrics_language'
                                allowClear={false}
                                width={'sm'}
                                initialValue={'el'}
                                showSearch
                                options={languageOptions}
                            />
                            <ProFormSelect
                                label='Price'
                                name='track_price_tier'
                                width={'sm'}
                                initialValue={'SINGLE2'}
                                options={[
                                    {label: 'Back: 0.69€ / 0.69$ / 0.59£', value: 'SINGLE1'},
                                    {label: 'Mid: 0.99€ / 0.99$ / 0.79£', value: 'SINGLE2'},
                                    {label: 'Front: 1.29€ / 1.29$ / 0.99£', value: 'SINGLE3'},
                                ]}
                            />
                            <ProFormRadio.Group
                                label='Parental Advisory'
                                name='parental_advisory'
                                allowClear
                                initialValue={'No'}
                                options={['Yes', 'No', 'Cleaned']}
                            />
                            <ProFormRadio.Group label='Instrumental' name='instrumental' initialValue={'No'}
                                                options={['Yes', 'No']}/>
                        </ProFormGroup>
                    </ProFormGroup>
                </div>
            )}
        </ProFormList>
    );
};

export default AddFormSecondPage;